/**
 * A little-endian cursor over an `ArrayBuffer` (or a view into one), for
 * decoding the fixed binary layouts used throughout the original Dynamix
 * 3Space file formats — RESOURCE volumes, `.TBL` models, `.TTM` scripts,
 * `.FNT` fonts, and friends.
 *
 * All multi-byte reads are little-endian, matching the DOS/x86 target the
 * original game data was authored for. Every read advances an internal
 * cursor and is bounds-checked: reading past the end of the reader's
 * window throws a `RangeError` instead of returning garbage, wrapping, or
 * reading adjacent memory. A failed read never moves the cursor, so a
 * caller can catch, seek elsewhere, and retry.
 *
 * This is the shared foundation every format decoder builds on; it knows
 * nothing about any particular file format.
 */
export class BinaryReader {
  private readonly view: DataView;
  private readonly length: number;
  private cursor = 0;

  /**
   * @param source Bytes to read. Either a raw `ArrayBuffer` or a typed
   *   array / `DataView` view into one — the reader's window starts at
   *   `source`'s own `byteOffset` in the latter case.
   * @param byteOffset Offset, relative to `source`, where the reader's
   *   window begins. Defaults to 0.
   * @param byteLength Length of the reader's window. Defaults to
   *   everything from `byteOffset` to the end of `source`.
   */
  constructor(source: ArrayBufferLike | ArrayBufferView, byteOffset = 0, byteLength?: number) {
    this.view = ArrayBuffer.isView(source)
      ? new DataView(
          source.buffer as ArrayBufferLike,
          source.byteOffset + byteOffset,
          byteLength ?? source.byteLength - byteOffset,
        )
      : new DataView(source, byteOffset, byteLength ?? source.byteLength - byteOffset);
    this.length = this.view.byteLength;
  }

  /** Total size of this reader's window, in bytes. */
  get byteLength(): number {
    return this.length;
  }

  /** Current cursor position, as a byte offset from the start of the window. */
  tell(): number {
    return this.cursor;
  }

  /** Number of unread bytes remaining in the window. */
  remaining(): number {
    return this.length - this.cursor;
  }

  /** Moves the cursor to an absolute byte offset within the window. */
  seek(offset: number): void {
    if (!Number.isInteger(offset) || offset < 0 || offset > this.length) {
      throw new RangeError(`BinaryReader.seek: offset ${offset} out of bounds [0, ${this.length}]`);
    }
    this.cursor = offset;
  }

  /** Moves the cursor forward (or backward, with a negative count) by `count` bytes. */
  skip(count: number): void {
    this.seek(this.cursor + count);
  }

  private need(size: number): void {
    if (this.cursor + size > this.length) {
      throw new RangeError(
        `BinaryReader: attempted to read ${size} byte(s) at offset ${this.cursor}, ` +
          `but only ${this.remaining()} remain (window length ${this.length})`,
      );
    }
  }

  /** Reads an unsigned 8-bit integer and advances the cursor by 1 byte. */
  u8(): number {
    this.need(1);
    const value = this.view.getUint8(this.cursor);
    this.cursor += 1;
    return value;
  }

  /** Reads a signed 8-bit integer and advances the cursor by 1 byte. */
  i8(): number {
    this.need(1);
    const value = this.view.getInt8(this.cursor);
    this.cursor += 1;
    return value;
  }

  /** Reads an unsigned little-endian 16-bit integer and advances the cursor by 2 bytes. */
  u16(): number {
    this.need(2);
    const value = this.view.getUint16(this.cursor, true);
    this.cursor += 2;
    return value;
  }

  /** Reads a signed little-endian 16-bit integer and advances the cursor by 2 bytes. */
  i16(): number {
    this.need(2);
    const value = this.view.getInt16(this.cursor, true);
    this.cursor += 2;
    return value;
  }

  /** Reads an unsigned little-endian 32-bit integer and advances the cursor by 4 bytes. */
  u32(): number {
    this.need(4);
    const value = this.view.getUint32(this.cursor, true);
    this.cursor += 4;
    return value;
  }

  /** Reads a signed little-endian 32-bit integer and advances the cursor by 4 bytes. */
  i32(): number {
    this.need(4);
    const value = this.view.getInt32(this.cursor, true);
    this.cursor += 4;
    return value;
  }

  /**
   * Reads `length` raw bytes and advances the cursor past them.
   *
   * The returned `Uint8Array` is a zero-copy view over the same
   * underlying buffer (like `Buffer.subarray`) — call `.slice()` on it if
   * you need bytes that outlive or are independent of the source buffer.
   */
  bytes(length: number): Uint8Array {
    if (!Number.isInteger(length) || length < 0) {
      throw new RangeError(`BinaryReader.bytes: length must be a non-negative integer, got ${length}`);
    }
    this.need(length);
    const start = this.view.byteOffset + this.cursor;
    const view = new Uint8Array(this.view.buffer, start, length);
    this.cursor += length;
    return view;
  }

  /**
   * Reads a C-style byte string.
   *
   * - With no argument: reads until a NUL (0x00) byte or the end of the
   *   window, whichever comes first, and consumes the NUL if one was
   *   found (it is not included in the result).
   * - With `maxLength`: always consumes exactly `maxLength` bytes (the
   *   fixed-size, NUL-padded char-array layout common in these formats,
   *   e.g. a 16-byte name field), returning everything before the first
   *   NUL within that span, or the full span if there is none.
   *
   * Bytes are decoded one-to-one into UTF-16 code units (byte value N
   * becomes `charCodeAt` N), *not* through a text encoding such as
   * Windows-1252 or CP437. The original game text is CP437, and several
   * CP437 code points fall in ranges other encodings treat as invalid or
   * remap losslessly to a different byte value, which round trips through
   * `String.fromCharCode`/`charCodeAt` corrupted. This reader stays
   * encoding-agnostic; a decoder that needs display text maps the raw
   * bytes through a CP437 table itself.
   */
  cstring(maxLength?: number): string {
    if (maxLength !== undefined) {
      const raw = this.bytes(maxLength);
      const nul = raw.indexOf(0);
      return rawString(nul === -1 ? raw : raw.subarray(0, nul));
    }

    const start = this.cursor;
    let end = start;
    while (end < this.length && this.view.getUint8(end) !== 0) {
      end++;
    }
    const start2 = this.view.byteOffset + start;
    const str = rawString(new Uint8Array(this.view.buffer, start2, end - start));
    this.cursor = end < this.length ? end + 1 : end;
    return str;
  }
}

/** Decodes bytes one-to-one into UTF-16 code units; see `BinaryReader.cstring`. */
function rawString(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += String.fromCharCode(bytes[i]!);
  }
  return out;
}
